import { inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';

import { round } from '@core/common';

type BigNumberTier = {
  scale: number;
  decimals: number;
  suffix: string;
};

const BIG_NUMBER_TIERS: BigNumberTier[] = [
  { scale: 1_000_000_000, decimals: 2, suffix: 'B' },
  { scale: 1_000_000, decimals: 2, suffix: 'M' },
  { scale: 1_000, decimals: 1, suffix: 'K' },
];

@Pipe({ name: 'laPreciseMetricValue' })
export class PreciseMetricValuePipe implements PipeTransform {
  locale = inject(LOCALE_ID);

  transform(value: number | string, decimalPlaces = 2, multiplier = 1): string | number {
    const parsedInput = this.parseInput(value, multiplier);
    if (parsedInput === null) {
      return '';
    }

    const parsedbsInput = Math.abs(parsedInput);
    const lt = parsedbsInput !== 0 && round(parsedbsInput, decimalPlaces) === 0;
    const minValue = 1 / Math.pow(10, decimalPlaces);

    return typeof parsedbsInput === 'number'
      ? lt
        ? `<${minValue.toLocaleString(this.locale, { maximumFractionDigits: decimalPlaces, minimumFractionDigits: decimalPlaces })}`
        : `${parsedbsInput.toLocaleString(this.locale, { maximumFractionDigits: decimalPlaces, minimumFractionDigits: decimalPlaces })}`
      : '';
  }

  private parseInput(value: string | number, multiplier: number): number | null {
    if (typeof value === 'number') {
      return value * multiplier;
    }

    const parsedFloat = parseFloat(value);
    if (!isNaN(parsedFloat)) {
      return parsedFloat * multiplier;
    }

    return null;
  }
}

@Pipe({ name: 'laBigMetricValue' })
export class BigMetricValuePipe implements PipeTransform {
  transform(value: number | string | null | undefined): string {
    if (value == null || value === '') {
      return '';
    }

    const parsed = typeof value === 'number' ? value : parseFloat(value);

    return Number.isFinite(parsed) ? formatBigMetricValue(parsed) : '';
  }
}

function formatBigMetricValue(raw: number): string {
  const sign = raw < 0 ? '-' : '';
  const abs = Math.abs(raw);

  if (abs < 1_000) {
    return `${sign}${Math.round(abs)}`;
  }

  let tierIndex = BIG_NUMBER_TIERS.findIndex((tier) => abs >= tier.scale);

  let { tier, rounded } = roundInTier(abs, tierIndex);

  if (rounded >= 1_000 && tierIndex > 0) {
    ({ tier, rounded } = roundInTier(abs, --tierIndex));
  }

  return `${sign}${formatBigNumberPart(rounded, tier.decimals)}${tier.suffix}`;
}

function roundInTier(abs: number, tierIndex: number): { tier: BigNumberTier; rounded: number } {
  const tier = BIG_NUMBER_TIERS[tierIndex];
  return { tier, rounded: round(abs / tier.scale, tier.decimals) };
}

function formatBigNumberPart(value: number, decimals: number): string {
  return parseFloat(value.toFixed(decimals)).toString();
}
